import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as React from 'react';
import { I18nextProvider } from 'react-i18next';
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  useRouteError
} from 'react-router';
import { Toaster } from 'sonner';
import './app.css';
import i18n from './lib/i18n.js';
import './lib/theme.js';

export const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false, staleTime: 15_000 } }
});

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="color-scheme" content="light dark" />
        <link rel="icon" href="/favicon.svg" type="image/svg+xml" />
        <title>Finokay</title>
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function Root() {
  React.useEffect(() => {
    const dark = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = () => document.documentElement.classList.toggle('dark', dark.matches);
    apply();
    dark.addEventListener('change', apply);
    return () => dark.removeEventListener('change', apply);
  }, []);
  return (
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <Outlet />
        <Toaster position="bottom-right" richColors closeButton />
      </QueryClientProvider>
    </I18nextProvider>
  );
}

export function HydrateFallback() {
  return <div className="p-8 text-sm text-muted-foreground">…</div>;
}

export function ErrorBoundary() {
  const error = useRouteError();
  const message = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? error.message
      : String(error);
  return (
    <main className="mx-auto max-w-lg p-8">
      <h1 className="text-lg font-semibold">{i18n.t('error.title')}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{message}</p>
      <a className="mt-4 inline-block text-sm text-primary underline" href="/">
        {i18n.t('error.home')}
      </a>
    </main>
  );
}
